import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { SubscriptionState, UUID } from "@/lib/types";

/** Follow/unfollow a row. Optimistic toggle with rollback. */
export function useRowSubscription(rowId: UUID | undefined) {
  const qc = useQueryClient();
  const key = queryKeys.rowSubscription(rowId ?? "none");

  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getRowSubscription(rowId as UUID),
    enabled: Boolean(rowId),
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      next ? api.subscribeRow(rowId as UUID) : api.unsubscribeRow(rowId as UUID),
    onMutate: async (next: boolean) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SubscriptionState>(key);
      qc.setQueryData<SubscriptionState>(key, { subscribed: next });
      return { prev };
    },
    onError: (_e, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  return {
    subscribed: query.data?.subscribed ?? false,
    toggle,
    busy: toggle.isPending,
  };
}
